import { Field, Form, Formik } from "formik";
import { ChangeEvent, MouseEventHandler, useCallback } from "react";
import { useDispatch } from "react-redux";

import { PatientGuarantor, PatientGuarantorWritable } from "pwapi";

import { UserLocation } from "custom-models";

import {
  updatePatientGuarantor,
  createPatientGuarantor,
} from "shared/actions/admin/adminPatientActions";
import {
  WORLDS_OLDEST_PERSONS_BIRTHDATE,
  PW_YOUNGEST_ACCEPTABLE_BIRTHDATE,
} from "shared/utils/dateUtils";
import { checkGuarantorAddress } from "shared/utils/patientUtils";
import usStates from "shared/utils/usStates";

import { PathologyWatchButton } from "components/customInputs/buttons/Button";
import CustomInput from "components/customInputs/CustomInput";
import DateInput from "components/customInputs/DateInput";
import SelectInput from "components/customInputs/SelectInput";
import TextInput from "components/customInputs/TextInput";

type GuarantorFormProps = {
  guarantor: PatientGuarantor;
  patientId: number;
  onClose: MouseEventHandler<HTMLButtonElement>;
  disableSubmit?: boolean;
};

const GuarantorForm = ({
  guarantor,
  patientId,
  onClose,
  disableSubmit = false,
}: GuarantorFormProps) => {
  const dispatch = useDispatch();

  const handleOnSubmit = useCallback(
    (values: PatientGuarantor) => {
      if (disableSubmit) return;
      // remove patient from the payload
      // eslint-disable-next-line unused-imports/no-unused-vars
      const { patient, ...newValues } = values;
      const address = checkGuarantorAddress(newValues.address);
      const payload: PatientGuarantorWritable & { onClose?: Function } =
        newValues.relationship_to_patient === "SELF"
          ? {
              id: guarantor?.id,
              relationship_to_patient: "SELF",
              patient_id: patientId,
              onClose,
            }
          : {
              ...guarantor,
              ...newValues,
              id: guarantor?.id,
              patient_id: patientId,
              address: address && {
                street: address.street || "",
                zipcode: address.zipcode || "",
                ...address,
                id: guarantor.address?.id,
              },
              onClose,
            };

      guarantor.id
        ? dispatch(updatePatientGuarantor(payload))
        : patientId && dispatch(createPatientGuarantor(payload));
    },
    [patientId, guarantor]
  );

  return (
    <Formik
      initialValues={{
        ...guarantor,
        relationship_to_patient:
          guarantor.relationship_to_patient === "SEL"
            ? "SELF"
            : guarantor.relationship_to_patient || undefined,
        address: { ...guarantor.address },
      }}
      enableReinitialize
      onSubmit={(values) => handleOnSubmit(values as PatientGuarantor)}
    >
      {({ resetForm, dirty, values, initialValues, setValues }) => {
        const requireAddressFields = checkGuarantorAddress(
          values?.address as UserLocation
        );
        return (
          <Form className="AdminPanelForm">
            <div className="panelContainer">
              <fieldset className="panelContainer__section">
                <legend className="panelContainer__section--title">
                  Guarantor Information
                </legend>
                <div className="panelContainer__inputGroup">
                  <Field name="relationship_to_patient">
                    {({ field }: any) => (
                      <SelectInput
                        label="Sub Relationship"
                        options={["SELF", "SPOUSE", "PARENT", "OTHER"].map(
                          (a) => ({
                            label: a,
                            value: a,
                          })
                        )}
                        {...field}
                        onChange={(e: ChangeEvent<HTMLInputElement>) => {
                          setValues({
                            // Only reset values if the user is swapping from SELF to something else, or something else to SELF.
                            ...((values.relationship_to_patient === "SELF" &&
                              e.target.value !== "SELF") ||
                            (values.relationship_to_patient !== "SELF" &&
                              e.target.value === "SELF")
                              ? Object.keys(initialValues).reduce(
                                  (acc: any, key) => {
                                    return { ...acc, [key]: "" };
                                  },
                                  {}
                                )
                              : values),
                            relationship_to_patient: e.target.value,
                          });
                        }}
                        disabled={disableSubmit}
                      />
                    )}
                  </Field>
                  <Field name="last_name">
                    {({ field }: any) => (
                      <TextInput
                        label="Last Name"
                        id="guarantor_last_name"
                        {...field}
                        disabled={
                          disableSubmit ||
                          values.relationship_to_patient === "SELF"
                        }
                      />
                    )}
                  </Field>
                  <Field name="first_name">
                    {({ field }: any) => (
                      <TextInput
                        label="First Name"
                        id="guarantor_first_name"
                        required
                        {...field}
                        disabled={
                          disableSubmit ||
                          values.relationship_to_patient === "SELF"
                        }
                      />
                    )}
                  </Field>
                  <Field name="middle_initial">
                    {({ field }: any) => (
                      <TextInput
                        label="M.I."
                        id="guarantor_middle_initial"
                        maxLength={1}
                        {...field}
                        disabled={
                          disableSubmit ||
                          values.relationship_to_patient === "SELF"
                        }
                      />
                    )}
                  </Field>
                </div>
                <div className="panelContainer__inputGroup">
                  <Field name="ssn">
                    {({ field }: any) => (
                      <TextInput
                        type="number"
                        label="SSN"
                        id="ssn"
                        maxLength="8"
                        {...field}
                        disabled={
                          disableSubmit ||
                          values.relationship_to_patient === "SELF"
                        }
                      />
                    )}
                  </Field>
                  <Field name="date_of_birth">
                    {({ field, form }: any) => (
                      <CustomInput label="Date of birth" id="date_of_birth">
                        <DateInput
                          {...field}
                          onChange={(e: ChangeEvent<HTMLInputElement>) =>
                            form.setFieldValue("date_of_birth", e.target.value)
                          }
                          disabled={
                            disableSubmit ||
                            values.relationship_to_patient === "SELF"
                          }
                          maxDate={PW_YOUNGEST_ACCEPTABLE_BIRTHDATE}
                          minDate={WORLDS_OLDEST_PERSONS_BIRTHDATE}
                        />
                      </CustomInput>
                    )}
                  </Field>
                  <Field name="sex">
                    {({ field }: any) => (
                      <div style={{ width: "4em", flex: "0 0 auto" }}>
                        <SelectInput
                          id="sex"
                          label="Sex"
                          {...field}
                          options={Object.values(PatientGuarantor.sex).map(
                            (s) => ({
                              label: s,
                              value: s,
                            })
                          )}
                          disabled={
                            disableSubmit ||
                            values.relationship_to_patient === "SELF"
                          }
                        />
                      </div>
                    )}
                  </Field>
                </div>
              </fieldset>
              <fieldset className="panelContainer__section">
                <legend className="panelContainer__section--title">
                  Guarantor Contact Information
                </legend>
                <div className="panelContainer__inputGroup">
                  <Field name="address.phone1">
                    {({ field }: any) => (
                      <TextInput
                        type="tel"
                        label="Phone"
                        id="guarantor_phone1"
                        {...field}
                        disabled={
                          disableSubmit ||
                          values.relationship_to_patient === "SELF"
                        }
                      />
                    )}
                  </Field>
                  <Field name="address.phone2">
                    {({ field }: any) => (
                      <TextInput
                        type="tel"
                        label="Phone 2"
                        id="guarantor_phone2"
                        {...field}
                        disabled={
                          disableSubmit ||
                          values.relationship_to_patient === "SELF"
                        }
                      />
                    )}
                  </Field>
                </div>
              </fieldset>
              <fieldset className="panelContainer__section">
                <legend className="panelContainer__section--title">
                  Guarantor Address
                </legend>
                <Field name="address.street">
                  {({ field }: any) => (
                    <TextInput
                      label="Street Line 1"
                      id="guarantor_street"
                      {...field}
                      required={
                        !!guarantor.address?.id || !!requireAddressFields
                      }
                      disabled={
                        disableSubmit ||
                        values.relationship_to_patient === "SELF"
                      }
                    />
                  )}
                </Field>
                <Field name="address.street2">
                  {({ field }: any) => (
                    <TextInput
                      label="Street Line 2"
                      id="guarantor_street2"
                      {...field}
                      disabled={
                        disableSubmit ||
                        values.relationship_to_patient === "SELF"
                      }
                    />
                  )}
                </Field>
                <div className="panelContainer__inputGroup">
                  <Field name="address.city">
                    {({ field }: any) => (
                      <TextInput
                        label="City"
                        id="city"
                        {...field}
                        disabled={
                          disableSubmit ||
                          values.relationship_to_patient === "SELF"
                        }
                      />
                    )}
                  </Field>
                  <Field name="address.state">
                    {({ field }: any) => (
                      <SelectInput
                        label="State"
                        options={Object.keys(usStates).map((abbr) => ({
                          label: usStates[abbr],
                          value: abbr,
                        }))}
                        {...field}
                        disabled={
                          disableSubmit ||
                          values.relationship_to_patient === "SELF"
                        }
                      />
                    )}
                  </Field>
                  <Field name="address.zipcode">
                    {({ field }: any) => (
                      <TextInput
                        label="Zip Code"
                        id="guarantor_zipcode"
                        {...field}
                        required={
                          !!guarantor.address?.id || !!requireAddressFields
                        }
                        disabled={
                          disableSubmit ||
                          values.relationship_to_patient === "SELF"
                        }
                      />
                    )}
                  </Field>
                </div>
              </fieldset>
            </div>
            <footer>
              <div className="panelContainer__inputGroup">
                <PathologyWatchButton onClick={onClose}>
                  Cancel
                </PathologyWatchButton>
              </div>

              <div className="panelContainer__inputGroup">
                <PathologyWatchButton
                  onClick={() => {
                    resetForm();
                  }}
                  disabled={!dirty}
                >
                  Discard Changes
                </PathologyWatchButton>
                <PathologyWatchButton
                  type="submit"
                  color="green"
                  disabled={!dirty || disableSubmit}
                >
                  Save
                </PathologyWatchButton>
              </div>
            </footer>
          </Form>
        );
      }}
    </Formik>
  );
};

export default GuarantorForm;
